import type { PaigeDeliveryState, PaigeExportQuery, PaigeExportRow } from './export-types';

/**
 * What the export history says about one delivery, in CapturePoint's words where it has them (its Activity:
 * "Exported", "Problem", "Returned to work", "Retry delivery", "Cancel delivery") and Paige's where Paige differs
 * (a destination per row; held by the job's export timing; Send back for review is ADR-0032's way to correct a
 * refused document). Pure: the screen and its tests read the same lines.
 */

/** A moment as people read it in the history: "Oct 20, 9:41 PM". */
export function whenOf(at: string): string {
  const date = new Date(at);
  return Number.isNaN(date.getTime())
    ? at
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

/** The row's state in a word or three. */
export function stateLabel(row: Pick<PaigeExportRow, 'state' | 'attempts' | 'releaseAt'>): string {
  switch (row.state) {
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Problem';
    case 'sending':
      return 'Sending now';
    case 'pending':
    case 'held':
      return waitingLabel(row);
    case 'skipped':
      return 'Not sent';
    case 'cancelled':
      return 'Cancelled';
    case 'superseded':
      return 'Replaced by a newer version';
  }
}

/** A delivery that waits: for a retry, for the job's export time, or for Export now. */
function waitingLabel(row: Pick<PaigeExportRow, 'state' | 'attempts' | 'releaseAt'>): string {
  if (row.state === 'pending') return row.attempts > 0 ? 'Trying again' : 'Waiting to send';
  return row.releaseAt ? `Goes ${whenOf(row.releaseAt)}` : 'Waits for Export now';
}

/** The tone of the row's state: what the badge looks like. */
export function stateTone(state: PaigeDeliveryState): 'ok' | 'problem' | 'waiting' | 'quiet' {
  if (state === 'delivered') return 'ok';
  if (state === 'failed') return 'problem';
  return state === 'held' || state === 'pending' || state === 'sending' ? 'waiting' : 'quiet';
}

/** The document's name as it was filed ("Acme_INV-1001.pdf") and the folders it went in, or its short id. */
export function nameOf(row: Pick<PaigeExportRow, 'filed' | 'documentId'>): {
  name: string;
  folders: string;
} {
  if (!row.filed) return { name: `Document ${row.documentId.slice(0, 8)}`, folders: '' };
  const parts = row.filed.split('/');
  return { name: parts.at(-1) ?? row.filed, folders: parts.slice(0, -1).join(' › ') };
}

/** Where it went: the destination's name, or what remains of one taken off the job. */
export function whereOf(row: Pick<PaigeExportRow, 'destinationLabel'>): string {
  return row.destinationLabel ?? 'A destination removed from the job';
}

export interface RowActions {
  retry: boolean;
  cancel: boolean;
  review: boolean;
}

/**
 * What a person may do on the row. Retry sends again what did not land (never a delivered one: ADR-0031); Cancel stops
 * what waits and is not on the wire; Send back for review opens a refused document in review, where a Confirm sends
 * the correction to the places that refused it (ADR-0032). Only someone who runs the job may act.
 */
export function actionsOf(row: PaigeExportRow, runsJobs: boolean): RowActions {
  const there = row.destinationLabel !== null && row.documentState !== 'cancelled';
  return {
    retry: runsJobs && there && ['failed', 'skipped', 'cancelled'].includes(row.state),
    cancel: runsJobs && !row.sending && ['held', 'pending', 'failed'].includes(row.state),
    review: row.state === 'failed' && ['accepted', 'exported'].includes(row.documentState),
  };
}

/** The status filter's choices, the value the API reads beside each. */
export const statusChoices: readonly { value: PaigeExportQuery['state'] | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Problem' },
  { value: 'waiting', label: 'Waiting to send' },
  { value: 'held', label: 'Waiting for the export time' },
  { value: 'skipped', label: 'Not sent' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** The day filter's choices: any date, today, yesterday and the five days before (CapturePoint's exportHistoryDayChoices). */
export function dayChoices(now = new Date()): { value: string; label: string }[] {
  const pad = (value: number) => String(value).padStart(2, '0');
  const days = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - index);
    const value = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
    const label =
      index === 0
        ? 'Today'
        : index === 1
          ? 'Yesterday'
          : day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    return { value, label };
  });
  return [{ value: '', label: 'Any date' }, ...days];
}
